import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { authApi } from '@/services/api'
import { setToken } from '@/lib/utils'
import { toast } from 'sonner'
import { Eye, EyeOff, Mail, Lock, Loader2 } from 'lucide-react'
import { motion } from 'framer-motion'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  // 2FA states
  const [requires2FA, setRequires2FA] = useState(false)
  const [tempToken, setTempToken] = useState('')
  const [twoFactorCode, setTwoFactorCode] = useState('')
  const [isBackupCode, setIsBackupCode] = useState(false)

  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!email || !password) {
      toast.error('Please fill in all fields')
      return
    }

    setLoading(true)
    try {
      const response = await authApi.login({ email, password })

      // Handle the response format from /api/v1/token/
      if (response.data['2fa_required'] && response.data.temp_token) {
        setTempToken(response.data.temp_token)
        setRequires2FA(true)
        toast.info('Two-factor authentication required')
      } else if (response.data.access) {
        // Store access token
        setToken(response.data.access)

        // Store refresh token
        if (response.data.refresh) {
          localStorage.setItem('refresh_token', response.data.refresh)
        }

        toast.success('Welcome back!')

        // Check for redirect path
        const redirectPath = localStorage.getItem('ldirect')
        if (redirectPath) {
          localStorage.removeItem('ldirect')
          navigate(redirectPath)
        } else {
          navigate('/')
        }
      } else {
        toast.error('Invalid response from server')
      }
    } catch (error) {
      console.error('Login error:', error)

      if (error.response?.status === 401) {
        toast.error('Invalid email or password')
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message)
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail)
      } else {
        toast.error('Failed to sign in. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handle2FASubmit = async (e) => {
    e.preventDefault()

    if (!twoFactorCode) {
      toast.error('Please enter your authentication code')
      return
    }

    setLoading(true)
    try {
      const response = await authApi.verifyLogin2FA(tempToken, twoFactorCode)

      if (response.data.access) {
        // Store access token
        setToken(response.data.access)

        // Store refresh token
        if (response.data.refresh) {
          localStorage.setItem('refresh_token', response.data.refresh)
        }

        toast.success('Welcome back!')

        // Check for redirect path
        const redirectPath = localStorage.getItem('ldirect')
        if (redirectPath) {
          localStorage.removeItem('ldirect')
          navigate(redirectPath)
        } else {
          navigate('/')
        }
      }
    } catch (error) {
      console.error('2FA Verification error:', error)
      toast.error(error.response?.data?.msg || 'Invalid authentication code')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-50 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8"
      >
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div data-logo-box className="w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-300">
            <span className="text-white font-bold text-2xl">N</span>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {requires2FA ? 'Two-Factor Authentication' : 'Welcome Back'}
          </h1>
          <p className="text-gray-600">
            {requires2FA ? 'Enter the security code from your authenticator app' : 'Sign in to continue to Newsne.ws'}
          </p>
        </div>

        {/* Form */}
        {!requires2FA ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="you@example.com"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              <label className="flex items-center">
                <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                <span className="ml-2 text-sm text-gray-600">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-primary hover:text-primary/80 font-medium">
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full mt-6"
              size="lg"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handle2FASubmit} className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  {isBackupCode ? 'Backup Code' : 'Authentication Code'}
                </label>
                <button
                  type="button"
                  onClick={() => setIsBackupCode(!isBackupCode)}
                  className="text-sm text-primary hover:text-primary/80"
                >
                  {isBackupCode ? 'Use Authenticator App' : 'Use Backup Code'}
                </button>
              </div>

              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono tracking-widest"
                  placeholder={isBackupCode ? "8-digit backup code" : "6-digit code"}
                  maxLength={isBackupCode ? 8 : 6}
                  disabled={loading}
                  autoComplete="one-time-code"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full mt-6"
              size="lg"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify & Sign In'
              )}
            </Button>

            <button
              type="button"
              onClick={() => {
                setRequires2FA(false)
                setTwoFactorCode('')
                setTempToken('')
              }}
              className="w-full mt-2 text-sm text-gray-600 hover:text-gray-900"
              disabled={loading}
            >
              Cancel and go back
            </button>
          </form>
        )}

        {/* Sign up link */}
        {!requires2FA && (
          <p className="text-center text-sm text-gray-600 mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary hover:text-primary/80 font-medium">
              Sign up
            </Link>
          </p>
        )}
      </motion.div>
    </div>
  )
}
